import { create } from '@bufbuild/protobuf'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import type { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type {
  SingularDay as ProtoSingularDay,
  SingularDayCategory,
  SingularDayDemandBehavior,
} from '@ebalance/proto/bffuser/v1/singular_day_model_pb'
import { listSingularDays } from '@ebalance/proto/bffuser/v1/singular_day_service-SingularDayService_connectquery'
import {
  ListSingularDaysRequestSchema,
  type ListSingularDaysResponse,
} from '@ebalance/proto/bffuser/v1/singular_day_service_pb'
import type { UseQueryResult } from '@tanstack/react-query'

import { fromProtoDate } from '~/models/dateTime'

/**
 * 特異日カレンダーマスタ一覧表示行型。
 *
 * Identity は surrogate `id` (UUID)。 `date` は immutable だが一意ではなく
 * (全エリア共通と特定エリアが同一日付に併存しうる)、 cache patch / 削除は
 * `id` 比較で行う。 `areaCodes` が空 = 全エリア共通、 1〜10 は特定エリア。
 */
export type SingularDayRow = {
  /** surrogate UUID identity (update/delete/cache のキー)。 */
  id: string
  /** `YYYY-MM-DD` (ローカルタイムゾーン非依存の日付キー)。 */
  dateKey: string
  date: Date
  name: string
  category: SingularDayCategory
  demandBehavior: SingularDayDemandBehavior
  /** 特異グループ番号 (1〜10)。 demandBehavior=SINGULAR の時のみ意味を持つ。 */
  singularGroup: number
  /** 空 = 全エリア共通、 1〜10 は特定エリア。 */
  areaCodes: AreaCode[]
  createdAt: Timestamp | undefined
  createdBy: string
  // optimistic lock 用に proto Timestamp で保持。 表示時のみ timestampDate で Date 変換。
  updatedAt: Timestamp | undefined
  updatedBy: string
}

/** `Date` → `YYYY-MM-DD` (ローカルタイムゾーン)。 Map key として使う。 */
export function dateKeyOf(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function convertItem(item: ProtoSingularDay): SingularDayRow {
  const date = item.date !== undefined ? fromProtoDate(item.date) : new Date(0)
  return {
    id: item.id,
    dateKey: dateKeyOf(date),
    date,
    name: item.name,
    category: item.category,
    demandBehavior: item.demandBehavior,
    singularGroup: item.singularGroup,
    areaCodes: item.areaCodes,
    createdAt: item.createdAt,
    createdBy: item.createdBy,
    updatedAt: item.updatedAt,
    updatedBy: item.updatedBy,
  }
}

export function convertListSingularDaysResponse(
  resp: ListSingularDaysResponse,
): SingularDayRow[] {
  return resp.singularDays.map(convertItem)
}

/**
 * 特異日一覧を取得する。 fiscalYear が指定されれば該当年度 (4/1〜翌3/31)、
 * 未指定なら全年度を返す (AC-MSD-001 / AC-MSD-003)。
 *
 * `throwOnError` は呼び出し側で上書きできる。 global QueryClient が
 * `throwOnError: true` なので、 **一覧が画面の主役でない補助用途** (対象日の特異日
 * 注記など) では `false` を渡すこと。 渡さないと取得失敗が render 中に throw して
 * router の `errorElement` = 全画面エラーへ飛び、 本来使えるはずの画面ごと消える
 * (`useListTradableAreas` と同じ fail-safe パターン)。
 */
export function useListSingularDays(
  fiscalYear?: number,
  options?: { throwOnError?: boolean },
): UseQueryResult<ListSingularDaysResponse, ConnectError> {
  return useQuery(
    listSingularDays,
    create(
      ListSingularDaysRequestSchema,
      fiscalYear !== undefined ? { fiscalYear } : {},
    ),
    options,
  )
}
